/**
 * This class is just like URLSearchParams, except that it does not percent-encode parameter names.
 * For example, URLSearchParams may produce this:
 *
 * filter%5B0%5D.value=im+a+flower
 *
 * whereas this class will produce:
 *
 * filter[0].value=im+a+flower
 *
 * .NET Core 6's default query binder does not decode query parameter names, and this is our
 * work-around to avoid changing APIs to POST, or implementing our own model binder (which is
 * high-risk).
 */
class URLSearchParamsWithoutKeyEncoding extends URLSearchParams {
  /**
   * Returns a string containing a query string suitable for use in a URL, but without encoding
   * parameter names. Does not include the question mark. */
  toString(): string {
    return Array.from(this.entries())
      .map(([key, value]) => `${key}=${encodeURIComponent(value).replaceAll('%20', '+')}`)
      .join('&');
  }
}

function serializeValue(into: URLSearchParamsWithoutKeyEncoding, value: unknown, key: string) {
  if (Array.isArray(value)) {
    value.forEach((item, index) => serializeValue(into, item, `${key}[${index}]`));
  } else if (Object.prototype.toString.call(value) === '[object Object]') {
    Object.keys(value).forEach((name) => serializeValue(into, value[name], `${key}.${name}`));
  } else if (value == null) {
    into.append(key, '');
  } else {
    into.append(key, String(value));
  }
}

// We use interfaces and classes throughout legacy code, which are not assignable to Record<string,
// unknown>, but are indeed compatible as far as we care. Provide an overload to handle this.

/** Similar to $.param(obj), but uses URLSearchParamsWithoutKeyEncoding for final stringifying. */
function serializeQueryWithoutKeyEncoding(data: Record<string, unknown>): string | undefined;
function serializeQueryWithoutKeyEncoding(data: object): string | undefined;

function serializeQueryWithoutKeyEncoding(
  data: Record<string, unknown> | null
): string | undefined {
  if (data == null) {
    return undefined;
  }
  const params = new URLSearchParamsWithoutKeyEncoding();
  Object.keys(data).forEach((key) => serializeValue(params, data[key], key));
  return params.toString();
}

export { URLSearchParamsWithoutKeyEncoding, serializeQueryWithoutKeyEncoding };
